import { Controller } from "@hotwired/stimulus";
import { Html5Qrcode } from "html5-qrcode";

// Matches ZOTP::Base32: A–Z / 2–7, optional padding (=), case-insensitive.
const BASE32_PATTERN = /^[A-Z2-7]+=*$/i;

export default class extends Controller {
  static targets = [
    "inputSection",
    "changeSection",
    "textInput",
    "qrSection",
    "qrToggle",
    "qrUpload",
    "qrFileInput",
    "hint",
    "error",
  ];

  static values = {
    showSecrets: Boolean,
    hasPrevalue: Boolean
  };

  connect() {
    this.isActivelyEditing = false;
    this.isOtpEdit = false;
    this.qrData = "";
    this.qrScannerSetup = false;

    this.form = this.element.closest("form");
    if (this.form) {
      this.boundValidateOnSubmit = this.validateOnSubmit.bind(this);
      this.form.addEventListener("submit", this.boundValidateOnSubmit);
    }

    this.syncServerErrorState();
    this.updateDisplayState();
  }

  syncServerErrorState() {
    if (!this.hasErrorTarget || this.errorTarget.classList.contains("hidden")) return;

    if (this.hasHintTarget) this.hintTarget.classList.add("hidden");
    this.textInputTarget.classList.add("otp-input__field--invalid");
    this.textInputTarget.setAttribute("aria-invalid", "true");
  }

  disconnect() {
    if (this.form && this.boundValidateOnSubmit) {
      this.form.removeEventListener("submit", this.boundValidateOnSubmit);
    }
  }

  formatInput(event) {
    const input = event.target;
    const formattedValue = input.value.toUpperCase().replace(/\s/g, '');

    if (!this.isActivelyEditing && !this.showSecretsValue && this.hasPrevalueValue) {
      this.textInputTarget.value = '';
    } else {
      input.value = formattedValue;
    }

    this.isActivelyEditing = true;
    this.clearError();
  }

  preventSpace(event) {
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  startEditing() {
    if (!this.showSecretsValue && this.hasPrevalueValue) {
      this.textInputTarget.value = '';
    }
    this.isActivelyEditing = true;
  }

  clear() {
    this.textInputTarget.value = '';
    this.clearError();
  }

  enableEdit() {
    this.isOtpEdit = true;
    this.updateDisplayState();

    this.textInputTarget.focus();
  }

  validateOnSubmit(event) {
    if (this.textInputTarget.disabled) return;

    // Don't re-check an existing secret that is only being re-displayed
    // (e.g. "show OTP secrets when editing") — that previously broke saves.
    if (this.hasPrevalueValue && !this.isActivelyEditing && !this.isOtpEdit) return;

    const value = this.textInputTarget.value.trim();
    if (!value || this.isValidBase32(value)) {
      this.clearError();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.showError();
    this.textInputTarget.focus();
  }

  isValidBase32(value) {
    return BASE32_PATTERN.test(value);
  }

  showError() {
    if (this.hasHintTarget) this.hintTarget.classList.add("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden");
    this.textInputTarget.classList.add("otp-input__field--invalid");
    this.textInputTarget.setAttribute("aria-invalid", "true");
  }

  clearError() {
    if (this.hasHintTarget) this.hintTarget.classList.remove("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
    this.textInputTarget.classList.remove("otp-input__field--invalid");
    this.textInputTarget.setAttribute("aria-invalid", "false");
  }

  toggleQrUpload() {
    const isHidden = this.qrUploadTarget.classList.contains('hidden');
    this.qrUploadTarget.classList.toggle('hidden', !isHidden);
    this.qrToggleTarget.classList.toggle('hidden', isHidden);

    if (isHidden) {
      this.setupQrScanner();
    }
  }

  setupQrScanner() {
    if (this.qrScannerSetup) return;

    const html5QrCode = new Html5Qrcode(this.qrFileInputTarget.id);

    this.qrFileInputTarget.addEventListener('change', (e) => {
      if (e.target.files.length === 0) return;

      const file = e.target.files[0];
      html5QrCode.scanFile(file, true)
        .then(decodedText => {
          this.qrData = decodedText;
        })
        .catch(err => {
          console.error('Error scanning file:', err);
        });
    });

    this.qrScannerSetup = true;
  }

  scanQrCode() {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    const url = this.qrData;
    const params = {};
    let match;

    while ((match = regex.exec(url))) {
      params[match[1]] = match[2];
    }

    if (params.secret) {
      const secret = params.secret.toUpperCase();
      this.textInputTarget.value = secret;
      this.clearError();
    } else {
      alert("Could not extract OTP secret!");
    }
  }

  updateDisplayState() {
    const shouldShowInput = !this.hasPrevalueValue || this.isOtpEdit || this.showSecretsValue;
    const shouldShowChangeButton = this.hasPrevalueValue && !this.isOtpEdit && !this.showSecretsValue;
    const shouldShowQrSection = !this.hasPrevalueValue || this.isOtpEdit || this.showSecretsValue;

    this.inputSectionTarget.classList.toggle('hidden', !shouldShowInput);
    this.changeSectionTarget.classList.toggle('hidden', !shouldShowChangeButton);
    this.qrSectionTarget.classList.toggle('hidden', !shouldShowQrSection);

    if (shouldShowQrSection) {
      this.qrToggleTarget.classList.remove('hidden');
      this.qrUploadTarget.classList.add('hidden');
    }

    // Disabled fields are not submitted, so we do not send otp_secret: "" when the secret
    // is intentionally hidden (would clear the stored OTP on save).
    this.textInputTarget.disabled = !shouldShowInput;

    const shouldShowValue = this.isActivelyEditing || this.showSecretsValue || !this.hasPrevalueValue;
    if (!shouldShowValue && this.hasPrevalueValue) {
      this.textInputTarget.value = '';
    }
  }
}
